import {getLocale} from './userLocale';

/**
 * Returns a string representing the date based on the provided zuluDateTime.
 *
 * If the zuluDateTime is the same as the current date, the function returns 'Today'.
 * If the zuluDateTime is the next day, the function returns 'Tomorrow'.
 * Otherwise, the function returns a string representing the date in the format 'Wed Dec 08 2021'.
 *
 * @param {string} zuluDateTime - The zulu datetime string to be converted to a date string.
 * @returns {string} - A string representing the date based on the provided zuluDateTime.
 */
const dateFromDateString = (zuluDateTime) => {
    const when = new Date(zuluDateTime).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    const tomorrow = new Date().setHours(24, 0, 0, 0);

    if (today === when) {
        return 'Today';
    } else if (tomorrow === when) {
        return 'Tomorrow';
    }

    return new Date(when).toDateString();
};


/**
 * Converts a given date object to a time string formatted based on the device's locale.
 *
 * @param {Date} date - The date object to be formatted.
 * @returns {string|undefined} - A string representing the time in the format of 'hour:numeric minute:numeric' based on the device's locale, or undefined if no date is provided.
 */
const getTimeBasedOnDeviceLocale = date => {
    if (!date) {
        return undefined;
    }
    const locale = getLocale();
    const dateOptions = {
        hour: 'numeric',
        minute: 'numeric',
    };

    return date.toLocaleTimeString(locale, dateOptions);
};

/**
 * Converts a given date to a string formatted based on the device's locale settings.
 *
 * @param {Date | string} date - The date to be formatted. Can be a Date object or an ISO date string.
 * @returns {string | undefined} The date formatted according to the device's locale, or undefined if no date is provided.
 */
const getDateBasedOnDeviceLocale = date => {
    if (!date) {
        return undefined;
    }

    let localDate;
    if (typeof date === 'string') {
        localDate = new Date(date);
    } else {
        localDate = date;
    }

    let options = { year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric' };
    return localDate.toLocaleDateString(getLocale(), options);
};

const hourMinutesFromDateString = (zuluDateTime) => {
    const theMoment = new Date(zuluDateTime);
    return getTimeBasedOnDeviceLocale(theMoment);
    /*
    const time = `${theMoment.getHours()}:${(
        '' + theMoment.getMinutes()
    ).padStart(2, '0')}`;
    return time;
    */
};

/**
 * Calculate start of delivery interval
 * @param zuluDateTime {Date} is the date object
 * @param start_moment {Date} is whether the delivery route has started or not
 * @param start_hour {Number} is the start hour of the shift
 * @param endTime {boolean} is whether the function is processing start or end delivery time
 * @param showHour {boolean} is whether to show the hour window or the 20 minutes window estimate
 * @return {string}
 */
const minimumHourMinutesFromDateString = (zuluDateTime, start_moment, start_hour= 15, endTime = false, showHour = false) => {
    const theMoment = prepareMinimumTimeOfDeparture(zuluDateTime, start_moment, start_hour, endTime);
    return getTimeBasedOnDeviceLocale(theMoment);
    /*
    const time = `${theMoment.getHours()}:${(
        '' + theMoment.getMinutes()
    ).padStart(2, '0')}`;
    return time;
    */
};

/**
 * Prepare delivery time intervall to show
 * @param zuluDateTime {Date} is the date object
 * @param start_moment {Date} is whether the delivery route has started or not
 * @param start_hour {Number} is the start hour of the shift
 * @param endTime {boolean} is whether the function is processing start or end delivery time
 * @return {Date}
 */
const prepareMinimumTimeOfDeparture = (zuluDateTime, start_moment = undefined, start_hour = 15, endTime = false) => {
    const theMoment = new Date(zuluDateTime);
    if (!start_moment && !endTime) {
        if (theMoment.getDay() > 0 && theMoment.getDay() < 6) {
            theMoment.setHours(start_hour, 0);
            /*
            if (theMoment.getHours() < 14 && theMoment.getHours() > 2) {
                theMoment.setHours(15, 0);
            }
            */
        } else {
            theMoment.setHours(start_hour, 0);
            /*
            if (theMoment.getHours() < 14 && theMoment.getHours() > 2) {
                theMoment.setHours(14, 0);
            }
            */
        }
    }
    return theMoment;
};

const getEstimation = (zuluDateTime) => {
    return prepareMinimumTimeOfDeparture(zuluDateTime);
};

/**
 * Returns the country code from the first segment of the current URL path.
 * Defaults to 'us' if missing. Supported: 'us', 'uae'.
 * @returns {string} 'us' | 'uae'
 */
const getCountryCode = () => {
    try {
        const segments = window.location.pathname.split('/').filter(Boolean);
        // Only treat first segment as country code if path has at least two segments (e.g., /us/:id)
        if (segments.length >= 2) {
            const code = (segments[0] || '').toLowerCase();
            if (code === 'uae' || code === 'us') {
                return code;
            }
        }
    } catch (e) {}
    return 'us';
};

const valueText = value => {
    if (value === null || value === undefined) return '';

    if (typeof value === 'string' || typeof value === 'number') {
        return String(value).trim();
    }

    if (typeof value === 'object') {
        return String(
            value.name || value.title || value.label || value.value || '',
        ).trim();
    }

    return String(value).trim();
};

const normalize = value =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

const containsText = (source, value) => {
    if (!source || !value) return false;
    return normalize(source).includes(normalize(value));
};

const isSameText = (a, b) => {
    if (!a || !b) return false;
    return normalize(a) === normalize(b);
};

const getAddressFields = a => {
    const locationAsText = typeof a?.location === 'string' ? a.location : null;

    return {
        buildingName: valueText(a?.buildingName),
        streetNumber: valueText(a?.streetNumber),
        street1: valueText(a?.street1 || a?.streetAddress1 || a?.street,),
        street2: valueText(a?.street2),
        area: valueText(a?.area),
        city: valueText(a?.city || locationAsText),
        postal: valueText(a?.postalCode || a?.zip || a?.zipCode),
        country: valueText(a?.country),
    };
};

const sanitizePublicAddressLine = value => {
    if (!value) return '';
    return String(value)
        // emails
        .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '')
        // UAE / international phone-ish numbers
        .replace(/(?:\+?\d[\d\s().-]{7,}\d)/g, '')
        // common phone labels
        .replace(/\b(?:tel|phone|mobile|mob|whatsapp|wa|call|contact)\s*[:-]?\s*/gi, '')
        // cleanup separators
        .replace(/\s*,\s*,+/g, ', ')
        .replace(/\s{2,}/g, ' ')
        .replace(/^[,\s-]+|[,\s-]+$/g, '')
        .trim();
};

const formatAddressMultiLine = a => {
    if (!a) return '';

    const {
        buildingName,
        streetNumber,
        street1,
        street2,
        area,
        city,
        postal,
        country,
    } = getAddressFields(a);

    const safeBuildingName = sanitizePublicAddressLine(buildingName);
    const safeStreetNumber = sanitizePublicAddressLine(streetNumber);
    const safeStreet1 = sanitizePublicAddressLine(street1);
    const safeStreet2 = sanitizePublicAddressLine(street2);
    const safeArea = sanitizePublicAddressLine(area);
    const safeCity = sanitizePublicAddressLine(city);
    const safePostal = sanitizePublicAddressLine(postal);
    const safeCountry = sanitizePublicAddressLine(country);

    const streetLine = sanitizePublicAddressLine([safeStreetNumber, safeStreet1].filter(Boolean).join(' '));

    const cityPostal = sanitizePublicAddressLine([safeCity, safePostal].filter(Boolean).join(', '));

    const lines = [];

    if (
        safeBuildingName &&
        !containsText(streetLine, safeBuildingName)
    ) {
        lines.push(safeBuildingName);
    }

    if (streetLine) { lines.push(streetLine); }

    if (
        safeStreet2 &&
        !containsText(streetLine, safeStreet2)
    ) {
        lines.push(safeStreet2);
    }

    if (
        safeArea &&
        !isSameText(safeArea, safeCity) &&
        !containsText(safeBuildingName, safeArea) &&
        !containsText(streetLine, safeArea) &&
        !containsText(safeStreet2, safeArea)
    ) {
        lines.push(safeArea);
    }

    if (
        cityPostal &&
        !containsText(streetLine, cityPostal)
    ) {
        lines.push(cityPostal);
    }

    if (
        safeCountry &&
        !containsText(streetLine, safeCountry) &&
        !containsText(cityPostal, safeCountry)
    ) {
        lines.push(safeCountry);
    }

    return lines
        .map(sanitizePublicAddressLine)
        .filter(Boolean)
        .join('\n');
};

const formatAddressSingleLine = a => {
    if (!a) return ''
    const {
        buildingName,
        streetNumber,
        street1,
        street2,
        area,
        city,
        postal,
        country,
    } = getAddressFields(a)

    const safeBuildingName = sanitizePublicAddressLine(buildingName)
    const safeStreetNumber = sanitizePublicAddressLine(streetNumber)
    const safeStreet1 = sanitizePublicAddressLine(street1)
    const safeStreet2 = sanitizePublicAddressLine(street2)
    const safeArea = sanitizePublicAddressLine(area)
    const safeCity = sanitizePublicAddressLine(city)
    const safePostal = sanitizePublicAddressLine(postal)
    const safeCountry = sanitizePublicAddressLine(country)

    const streetLine = sanitizePublicAddressLine(
        [safeStreetNumber, safeStreet1].filter(Boolean).join(' ')
    )

    const cityPostal = sanitizePublicAddressLine(
        [safeCity, safePostal].filter(Boolean).join(', ')
    )

    const parts = []

    if (
        safeBuildingName &&
        !containsText(streetLine, safeBuildingName)
    ) {
        parts.push(safeBuildingName)
    }

    if (streetLine) {
        parts.push(streetLine)
    }

    if (
        safeStreet2 &&
        !containsText(streetLine, safeStreet2)
    ) {
        parts.push(safeStreet2)
    }

    if (
        safeArea &&
        !isSameText(safeArea, safeCity) &&
        !containsText(safeBuildingName, safeArea) &&
        !containsText(streetLine, safeArea) &&
        !containsText(safeStreet2, safeArea)
    ) {
        parts.push(safeArea)
    }

    if (
        cityPostal &&
        !containsText(streetLine, cityPostal)
    ) {
        parts.push(cityPostal)
    }

    if (
        safeCountry &&
        !containsText(streetLine, safeCountry) &&
        !containsText(cityPostal, safeCountry)
    ) {
        parts.push(safeCountry)
    }

    return parts
        .map(sanitizePublicAddressLine)
        .filter(Boolean)
        .join(', ')
}

export {
    dateFromDateString,
    hourMinutesFromDateString,
    getEstimation,
    minimumHourMinutesFromDateString,
    prepareMinimumTimeOfDeparture,
    getDateBasedOnDeviceLocale,
    getCountryCode,
    formatAddressMultiLine,
    formatAddressSingleLine,
};
